import { normalizeText } from "utils/text.normalize.logic";

export interface UnitTeamOption {
  readonly entityId?: number;
  readonly entityName?: string | null;
  readonly teams: ReadonlyArray<{
    readonly id: number;
    readonly name: string | null;
  }>;
  readonly unitId: number;
  readonly unitName: string | null;
}

export interface EntityUnitTeamOption {
  readonly entityId: number;
  readonly entityName: string | null;
  readonly units: readonly UnitTeamOption[];
}

export interface UnitTeamEntityOption {
  readonly id: number;
  readonly name: string | null;
}

interface FilterUnitTeamOptionsParams {
  readonly options: readonly UnitTeamOption[];
  readonly query: string;
  readonly unknownTeamLabel: string;
  readonly unknownUnitLabel: string;
}

interface FilterEntityUnitTeamOptionsParams extends FilterUnitTeamOptionsParams {
  readonly entities: readonly UnitTeamEntityOption[];
}

interface GroupUnitTeamOptionsByEntityParams {
  readonly entities?: readonly UnitTeamEntityOption[];
  readonly options: readonly UnitTeamOption[];
}

export function groupUnitTeamOptionsByEntity({
  entities: entityOptions = [],
  options,
}: GroupUnitTeamOptionsByEntityParams): EntityUnitTeamOption[] {
  const entities = new Map<number, EntityUnitTeamOption>();

  entityOptions.forEach((entity) => {
    entities.set(entity.id, {
      entityId: entity.id,
      entityName: entity.name,
      units: [],
    });
  });

  options.forEach((option) => {
    if (option.entityId == null) return;
    const current = entities.get(option.entityId);
    entities.set(option.entityId, {
      entityId: option.entityId,
      entityName: option.entityName ?? current?.entityName ?? null,
      units: current ? [...current.units, option] : [option],
    });
  });

  return Array.from(entities.values());
}

export function filterEntityUnitTeamOptions({
  entities,
  options,
  query,
  unknownTeamLabel,
  unknownUnitLabel,
}: FilterEntityUnitTeamOptionsParams): EntityUnitTeamOption[] {
  const filteredOptions = filterUnitTeamOptions({
    options,
    query,
    unknownTeamLabel,
    unknownUnitLabel,
  });
  const normalizedQuery = normalizeText(query);
  const filteredEntityIds = new Set(
    filteredOptions.flatMap((option) =>
      option.entityId == null ? [] : [option.entityId],
    ),
  );
  const filteredEntities = entities.filter(
    (entity) =>
      !normalizedQuery ||
      normalizeText(entity.name ?? "").includes(normalizedQuery) ||
      filteredEntityIds.has(entity.id),
  );

  return groupUnitTeamOptionsByEntity({
    entities: filteredEntities,
    options: filteredOptions,
  });
}

export function filterUnitTeamOptions({
  options,
  query,
  unknownTeamLabel,
  unknownUnitLabel,
}: FilterUnitTeamOptionsParams): UnitTeamOption[] {
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) return [...options];

  return options.flatMap((option) => {
    const entityName = (option.entityName ?? "").trim();
    const unitName = (option.unitName ?? "").trim() || unknownUnitLabel;
    const isParentMatch = [entityName, unitName].some((name) =>
      normalizeText(name).includes(normalizedQuery),
    );
    if (isParentMatch) return [option];

    const teams = option.teams.filter((team) =>
      normalizeText((team.name ?? "").trim() || unknownTeamLabel).includes(
        normalizedQuery,
      ),
    );
    return teams.length > 0 ? [{ ...option, teams }] : [];
  });
}
